import { demo } from './demo';
import { pricingCard, pricing, usd } from './pricing';

/**
 * Hero copy (plan/05 §3, refreshed). Voice: a senior installer who's also a
 * geometer — concrete, active, no banned words. The hero shows the REAL product
 * editor, not an abstraction; the copy names the pain and the win in installer
 * language.
 */
export const hero = {
	/** Mono kicker — states the core transform (3D → 2D) in the readout voice. */
	kicker: '3D scan → 2D cutting template',
	/** The site-wide positioning line (content/demo.ts), so every entry page says the same thing. */
	headline: demo.positioning,
	/** The homepage h1 (Workbench, 2026-10-13): the positioning line, cut to fit over the live editor. */
	homeHeadline: 'Cut the pattern your library doesn’t have.',
	/** Kept in reserve for A/B (do not delete). */
	headlineAlternates: [
		'Turn any car into your next cut file.',
		'The library is late. The car is here.',
		'Scan it in 3D. Cut it flat.',
		'Create the pattern the library cannot supply.'
	],
	subline:
		'PPF pattern software for custom, missing and early vehicle templates. Turn your own 3D scans into measured, editable, plotter-ready cut files. Any car. Any panel. No catalog wait.',
	cta: pricingCard.cta,
	/** Secondary hero action → the workflow-demo enquiry, for visitors not ready to pre-order. */
	secondaryCta: demo.cta,
	secondaryHref: '/ppf-pattern-software#demo',
	/** Value stack shown beside the claim, before the feature story begins. */
	valuePoints: [
		{ label: 'Say yes to unlisted cars.', detail: ' New models, facelifts and one-off panels.' },
		{
			label: 'Design coverage, not compromises.',
			detail: ' Custom seams, wraps and partial fronts.'
		},
		{
			label: 'Catch waste before the plotter.',
			detail: ' Stretch, scale and edge checks in pre-flight.'
		}
	],
	founderTerm: `${usd(pricing.founderYear)} / 12 months`,
	/** The output path — installers need to know it ends at their plotter. */
	outputFormats: 'SVG · DXF · PLT · Graphtec FC9000',
	/** Mono trust line beneath the CTA — the offer, framed as the absence of a trap.
	    NBSP before each middot: a wrap never leaves a separator leading a line. */
	trustLine: `${usd(pricing.founderYear)} first year\u00A0· annual price locked\u00A0· no auto-renew\u00A0· refundable until delivery`,
	/** CAD-style callouts anchored to the product shot — this audience trusts numbers. */
	specs: [
		{ label: 'Scan mesh', value: '23,849 faces' },
		{ label: 'Template size', value: '608 × 229 mm' },
		{ label: 'Within 8% stretch', value: '>99.9% by area' }
	],
	/**
	 * The hero canvas performs the core transform on one real part — a Volvo FH
	 * A-pillar trim scanned in the studio (see content/proof.ts): a static INPUT
	 * panel (the 3D scan, carrying the meaningful alt — this is the LCP image) beside
	 * an OUTPUT panel that cross-fades through the flatten → shear-check → template
	 * sequence. Every frame is the real part, not an illustration.
	 */
	visual: {
		scanCaption: '3D scan · Volvo FH trim',
		patternCaption: '2D cutting template · plotter-ready',
		editorAlt:
			'PPF Engine desktop editor showing a real Volvo FH trim scan flattened into a measured plotter-ready PPF cutting template.',
		scanAlt:
			'A real 3D scan of a Volvo FH A-pillar trim panel, a long compound-curved strip rendered in graphite with a chartreuse rim light: the input PPF Engine flattens into a cutting template.',
		outputStages: [
			{
				asset: 'real-flatwire',
				tab: 'Flatten',
				caption: 'flattened · the real mesh, true to the panel',
				w: 1600,
				h: 603
			},
			{
				asset: 'real-shear',
				tab: 'Inspect',
				caption: 'shear check · in the editor, before you cut',
				w: 1080,
				h: 620
			},
			{
				asset: 'real-template',
				tab: 'Cut path',
				caption: '2D template · this exact piece was cut',
				w: 2400,
				h: 904
			}
		]
	},
	inspector: [
		{ label: 'Boundary', value: '< 0.3 mm', accent: true },
		{ label: 'Tangent error', value: '< 1°', accent: false },
		{ label: 'Crossed lines', value: '0', accent: true },
		{ label: 'Scale check', value: 'Passed', accent: true },
		{ label: 'Material', value: 'PPF · 190 µm', accent: false }
	]
} as const;

/**
 * 01 / WHY SHOPS PAY FOR IT — the value section right after the hero (live-site
 * copy). The three points ARE `hero.valuePoints`; the framing lines are restored
 * from the deployed page. It leads on the money argument, then the price it costs.
 */
export const whyShops = {
	index: '01',
	label: 'Live job',
	heading: 'Why shops pay for it',
	intro: 'One recovered full-front job can pay for your first year of PPF Engine.',
	points: hero.valuePoints,
	priceLabel: `Presale access ${usd(pricing.founderYear)} / 12 months`
} as const;

/**
 * The six tools on the app window's rail — the real pipeline, in order. Shared by
 * the Hero and Process windows so the rail means the same thing all the way down
 * the page: the lit tool is the step the geometry is at right now.
 */
export const windowTools = [
	{ glyph: 'scan', label: 'Scan' },
	{ glyph: 'clean', label: 'Clean' },
	{ glyph: 'seam', label: 'Seam' },
	{ glyph: 'flatten', label: 'Flatten' },
	{ glyph: 'refine', label: 'Refine' },
	{ glyph: 'cut', label: 'Cut' }
] as const;

/**
 * The hero window's four states — the whole product in one control, on one real
 * part (the Volvo FH A-pillar trim). Each is a genuine render from that job, not
 * an illustration; `tool` lights the matching rail glyph, and `inspector` is what
 * the app would actually be reporting at that moment.
 *
 * The scan is first because it's the LCP image and the honest start of the story:
 * the input is a car, not a catalogue entry.
 */
export const heroStages = [
	{
		asset: 'real-scan',
		tab: 'Scan',
		tool: 0,
		w: 1919,
		h: 715,
		alt: hero.visual.scanAlt,
		status: ['volvo-fh-aero · a-pillar trim', 'mesh · 23,849 faces', 'imported'],
		inspector: [
			{ label: 'Source', value: 'STL · scan', accent: false },
			{ label: 'Faces', value: '23,849', accent: true },
			{ label: 'Panel', value: 'A-pillar trim', accent: false },
			{ label: 'State', value: '3D surface', accent: false }
		]
	},
	{
		asset: 'real-flatwire',
		tab: 'Flatten',
		tool: 3,
		w: 1600,
		h: 603,
		alt: 'The same Volvo FH trim panel flattened in PPF Engine, shown as the real mesh wireframe laid out true to the panel.',
		status: ['volvo-fh-aero · a-pillar trim', 'flatten · one pass', '2.8 s'],
		inspector: [
			{ label: 'Flatten', value: 'one pass', accent: false },
			{ label: 'Within 8%', value: '> 99.9%', accent: true },
			{ label: 'Top edge', value: '142.2 cm', accent: false },
			{ label: 'State', value: '2D · measured', accent: false }
		]
	},
	{
		asset: 'real-shear',
		tab: 'Inspect',
		tool: 4,
		w: 1080,
		h: 620,
		alt: 'PPF Engine’s shear check on the flattened Volvo FH trim, grading where the film would stretch or compress before any film is cut.',
		status: ['volvo-fh-aero · a-pillar trim', 'shear check', 'no relief cuts needed'],
		inspector: [
			{ label: 'Worst shear', value: 'within tol.', accent: true },
			{ label: 'Stretch', value: 'graded', accent: false },
			{ label: 'Relief cuts', value: '0 needed', accent: true },
			{ label: 'State', value: 'checked', accent: false }
		]
	},
	{
		asset: 'real-template',
		tab: 'Cut path',
		tool: 5,
		w: 2400,
		h: 904,
		alt: 'The finished plotter-ready cutting template for the Volvo FH A-pillar trim, a clean chartreuse cut path with registration marks.',
		status: ['volvo-fh-aero_a-pillar.svg', 'ready · Graphtec FC9000', 'this piece was cut'],
		inspector: hero.inspector
	}
] as const;

/**
 * The three scroll chapters over the morph (SCAN → FLATTEN → CUT), matching the
 * shader states in plan/03 §3. The morph performs the product's core transform.
 */
export const morphChapters = [
	{
		index: '01',
		label: 'Scan',
		heading: 'Start from the car, not a catalog.',
		body: 'Any scanner that outputs a mesh, from an entry-level structured-light handheld to an Artec. The scan informs the design; it doesn’t dictate it.'
	},
	{
		index: '02',
		label: 'Flatten',
		heading: 'See the stretch before it becomes a wrinkle.',
		body: 'Conformal flattening with honest distortion maps: red where the film compresses, blue where it stretches. It measures every result, not just the easy ones.'
	},
	{
		index: '03',
		label: 'Cut',
		heading: 'A template your plotter already understands.',
		body: 'Clean Bézier boundaries, registration marks, a 100 mm scale line. Exports to SVG, DXF and PLT.'
	}
] as const;

/** 02 / THE PROBLEM — short and sharp (plan/04 §02, plan/05 §4). */
export const problem = {
	index: '02',
	label: 'The leverage',
	heading: 'Pattern freedom changes what your shop can say yes to.',
	paragraphs: [
		'Pattern libraries make ordinary work convenient. They do not help when the new model is early, the panel is rare, or the customer wants coverage nobody has drawn.',
		'PPF Engine turns the car in your bay into the source of truth. You control the coverage, see the material risk before cutting, and keep the reusable pattern.'
	],
	outcomes: [
		{
			index: '01',
			label: 'Win the unavailable job',
			value: 'Any car',
			body: 'Build the pattern while the vehicle is in your shop instead of waiting for a catalog update.'
		},
		{
			index: '02',
			label: 'Sell custom coverage',
			value: 'Any panel',
			body: 'Place the seam and coverage line where the customer needs it, then save it for the next car.'
		},
		{
			index: '03',
			label: 'Protect your margin',
			value: 'Before cut',
			body: 'Inspect stretch, dimensions and vector quality before a metre of film comes off the roll.'
		}
	]
} as const;
